import { useCallback } from 'react';
import { showApolloErrorToUser } from '@pletly/ui';
import {
	deleteToken,
	saveToken,
	saveRefreshToken,
	saveMediaToken,
	renewToken,
	getUserFromToken,
} from '../../helpers/auth';
import { client } from './../../api/apollo';
import { Action } from './store/reducer';
import { useLogin } from './useLogin';
import { useRegister } from './useRegister';
import { setCallbackUrl } from './store/callbackUrl';
import { useMultiTabLogout } from './events/logout';

export const useAuthFunctions = (
	dispatch: React.Dispatch<Action>,
	options?: { onLogin?: () => void | Promise<void>; beforeLogOut?: () => void | Promise<void> },
) => {
	const { onLogin, beforeLogOut } = options ?? {};
	const { handleLogin, abortLogin } = useLogin();
	const { handleRegister, abortRegister } = useRegister();
	const { sendLogoutEvent, sendLoginEvent } = useMultiTabLogout();

	const login = async (data: Parameters<typeof handleLogin>[0]) => {
		const {
			accessToken: token,
			refreshToken,
			mediaToken,
			user,
			callbackUrl,
		} = await handleLogin(data);
		const { features } = getUserFromToken(token);

		await saveToken(token);
		await saveRefreshToken(refreshToken);
		await saveMediaToken(mediaToken);
		onLogin?.();

		await setCallbackUrl(callbackUrl);
		dispatch({
			type: 'SIGN_IN',
			token,
			mediaToken,
			user,
			features,
			callbackUrl,
		});

		return sendLoginEvent();
	};

	const register = async (data: Parameters<typeof handleRegister>['0']) => {
		try {
			const { accessToken: token, refreshToken, mediaToken, user } = await handleRegister(data);
			const { features } = getUserFromToken(token);

			await saveToken(token);
			await saveRefreshToken(refreshToken);
			await saveMediaToken(mediaToken);
			onLogin?.();

			dispatch({
				type: 'SIGN_IN',
				token,
				mediaToken,
				user,
				features,
			});
		} catch (error) {
			console.error(error);
			showApolloErrorToUser(error as Error);
		}
	};

	const logout = useCallback(
		async (allTabs = true) => {
			try {
				beforeLogOut && (await beforeLogOut());
			} catch (error) {
				console.error(error);
			}
			await Promise.all([deleteToken(), client.clearStore()]);

			dispatch({ type: 'SIGN_OUT' });

			if (allTabs) {
				return sendLogoutEvent();
			}
		},
		[beforeLogOut, dispatch, sendLogoutEvent],
	);

	const refreshUserToken = async () => {
		const tokens = await renewToken(client);
		if (!tokens?.accessToken) {
			return;
		}
		const { accessToken, mediaToken } = tokens;

		const { userId, uuid, profile, features } = getUserFromToken(accessToken);
		const user = { ...profile, id: userId.toString(), uuid };

		dispatch({ type: 'RESTORE_TOKEN', token: accessToken, mediaToken, user, features });
	};

	return { login, abortLogin, register, abortRegister, logout, renewToken: refreshUserToken };
};
